import React, { Suspense, useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';

import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { CardAction } from '@atlaskit/smart-card';
import { AnyAri } from '@atlassian/ari/any-ari';
import {
	type FocusAreaHealthKey,
	FocusAreaIcon,
	type FocusAreaStatusKey,
} from '@atlassian/focus-area-icon';
import {
	isSearchAssetsPartial,
	isSearchResultAssetsObject,
	isSearchResultAssetsObjectSchema,
	isSearchResultAssetsObjectType,
	type SearchAssetsPartial,
} from '@atlassian/search-client/assets/assets';
import { isSearchAtlasPartial, type SearchAtlasPartial } from '@atlassian/search-client/atlas';
import {
	SearchConfluenceSubtype,
	type SearchResultType,
} from '@atlassian/search-client/common/types';
import {
	isSearchConfluencePageBlogAttachment,
	isSearchConfluencePartial,
	type SearchConfluencePartial,
} from '@atlassian/search-client/confluence';
import {
	isSearchJiraPartial,
	isSearchResultJiraBoard,
	isSearchResultJiraIssue,
	type SearchJiraPartial,
} from '@atlassian/search-client/jira';
import {
	isSearchMercuryPartial,
	isSearchResultMercuryFocusAreaPartial,
	isSearchResultMercuryFocusAreaStatusUpdatePartial,
	isSearchResultSpfAskPartial,
	type SearchMercuryPartial,
} from '@atlassian/search-client/mercury';
import { type SearchPageNode } from '@atlassian/search-client/search-page/types';
import { isSearchTalentPartial, type SearchTalentPartial } from '@atlassian/search-client/talent';
import {
	isCodeResult,
	isDocumentEntity,
	isPullRequestEntity,
	isSearchResultGraphEntity,
	isWorkItemEntity,
	type SearchResultEntity,
	type SearchResultGraphCode,
	type SearchResultGraphDocument,
} from '@atlassian/search-client/third-party-graph-entity';
import {
	getConfluenceUrlWithAnalyticsQueryParams,
	getGenericUrlWithAnalyticsQueryParams,
} from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { getThirdPartyConfigs as getConstantThirdPartyConfigs } from '../../../../common/constants/3p-product-configs';
import { ReturnIcon } from '../../../../common/constants/icons/return';
import { is3pProductKeyForAnalytics } from '../../../../common/constants/is3p-product-key-for-analytics';
import {
	isProductKey,
	ProductKeys,
	ProductKeys1PForAnalytics,
	type ProductKeys3P,
} from '../../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { SearchExperience } from '../../../../common/constants/search-experience';
import { getSubtypeLabel } from '../../../../common/ui/3p-result-labeling';
import {
	type ActivityEventType,
	EventTypeMessage,
	VIEWED_EVENT,
	WORKED_EVENT,
} from '../../../../common/ui/event-type-message';
import { GraphWorkItemStatusLozenge } from '../../../../common/ui/graph-work-item-status';
import { getStatus } from '../../../../common/ui/jira-status';
import {
	getAssetsEntityIcon,
	getAtlasEntityIcon,
	getConfluenceEntityIcon,
	getJiraEntityIcon,
} from '../../../../common/ui/product-entity-icon';
import { ProductIcon } from '../../../../common/ui/product-icon';
import { useItemsAnalyticsContext } from '../../../../common/ui/quick-find/items-analytics-provider';
import {
	SearchResult,
	type SearchResultEventArguments,
} from '../../../../common/ui/quick-find/search-dialog-result';
import { type SearchMetadataPart } from '../../../../common/ui/search-metadata';
import { SearchResultType as SearchResultTypeText } from '../../../../common/ui/search-result-type';
import { TalentUserAvatar } from '../../../../common/ui/talent-user-avatar';
import { getJiraSearchResultTypeMetadata } from '../../../../common/utils/get-jira-search-result-type-metadata';
import { isDummyBoardUrl } from '../../../../common/utils/is-dummy-board-url';
import { isProductImpersonated } from '../../../../common/utils/is-product-impersonated';
import { normalizeWorkItemStatusLabel } from '../../../../common/utils/normalize-work-item-status-label';
import {
	getPullRequestStatusAppearance,
	getPullRequestStatusMessage,
} from '../../../../common/utils/pull-request-status';
import { type State } from '../../../../common/utils/quick-find/state';
import { get3PProductFromProviderIdOrIntegrationId } from '../../../../common/utils/search-result-utils';
import { getSummarizedAdfContent } from '../../../../common/utils/summarized-adf-content';
import {
	getResultConnectorSource,
	isResultSmartLink,
} from '../../../../common/utils/third-party-utils';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { type QuickFindResultEvent } from '../../../../controllers/quick-find/utils';
import {
	useBootstrap,
	usePrimaryProduct,
	usePrimarySubProduct,
	useSearchSessionId,
} from '../../../../controllers/store/bootstrap';
import { useQuickFindActions, useQuickFindQuery } from '../../../../controllers/store/quick-find';
import { useIsJiraHydrationLoading } from '../../../../controllers/store/search';

import { messages } from './messages';

const HoverCard = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_smart-card/hover-card" */ '@atlaskit/smart-card/hover-card'
	).then((mod) => ({ default: mod.HoverCard })),
);

const typeLabelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

const mapLatestUserActionToActivityEventType = (
	latestUserAction: string | undefined,
): ActivityEventType => {
	switch (latestUserAction?.toLowerCase()) {
		case 'viewed':
			return VIEWED_EVENT.VIEWED;
		case 'commented':
			return WORKED_EVENT.COMMENTED;
		case 'edited':
			return WORKED_EVENT.EDITED;
		case 'shared':
			return WORKED_EVENT.SHARED;
		default:
			return WORKED_EVENT.UPDATED;
	}
};

const mapConfluenceSearchResult = (
	result: SearchConfluencePartial,
	query: string,
	searchSessionId: string,
) => {
	const id = result.id;
	const title = result.title;
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const space =
		isSearchConfluencePageBlogAttachment(result) && result.space?.name ? result.space.name : null;
	const spaceId = isSearchConfluencePageBlogAttachment(result) && result.space?.id;

	const isLiveDoc =
		isSearchConfluencePageBlogAttachment(result) &&
		result.confluenceEntity?.subtype === SearchConfluenceSubtype.LIVE &&
		fg('confluence_live_pages_open_beta_trait_opted_in');

	const icon = getConfluenceEntityIcon({
		iconUrl: result.iconUrl,
		type: result.type,
		isLiveDoc,
	});

	const resultType = isLiveDoc ? <FormattedMessage {...messages.liveDoc} /> : result.type;
	const metadata = [
		'Confluence',
		[
			'type',
			<Box xcss={typeLabelContainer}>
				<SearchResultTypeText
					type={result.type as SearchResultType}
					defaultMessage={resultType}
					subtype={
						isSearchConfluencePageBlogAttachment(result)
							? result.confluenceEntity?.subtype
							: undefined
					}
				/>
			</Box>,
		],
		space,
	] satisfies SearchMetadataPart[];

	// Use user action data if available, otherwise fall back to lastModified
	const timestamp = result.latestUserActionTs || result.lastModified || '';
	const eventType = result.latestUserAction
		? mapLatestUserActionToActivityEventType(result.latestUserAction)
		: WORKED_EVENT.UPDATED;

	const rightMetadata = timestamp ? (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={false} />
	) : null;
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const url = getConfluenceUrlWithAnalyticsQueryParams({ url: result.url, query, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		containerId: spaceId,
		contentType: `${ProductKeys.Confluence} ${result.type}`,
		product: ProductKeys.Confluence,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapAtlasSearchResult = (
	result: SearchAtlasPartial,
	query: string,
	searchSessionId: string,
) => {
	const id = result.id;

	const icon = getAtlasEntityIcon({
		iconUrl: result.iconUrl,
		type: result.type,
	});

	const title = result.title;
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const metadata = [
		'Atlas',
		[
			'type',
			<Box xcss={typeLabelContainer}>
				<SearchResultTypeText type={result.type as SearchResultType} defaultMessage={result.type} />
			</Box>,
		],
	] satisfies SearchMetadataPart[];

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	const url = getGenericUrlWithAnalyticsQueryParams({ url: result.url, searchSessionId });

	let product: string = ProductKeys.Atlas;
	if (fg('rovo_search_cross_product_url_wrapping')) {
		if (result.type === 'goal') {
			product = ProductKeys1PForAnalytics.AtlasGoals;
		} else if (result.type === 'project') {
			product = ProductKeys1PForAnalytics.AtlasProjects;
		}
	}

	const analyticsMetadata = {
		contentId: id,
		contentType: `${ProductKeys.Atlas} ${result.type}`,
		product,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapMercurySearchResult = (
	result: SearchMercuryPartial,
	query: string,
	searchSessionId: string,
) => {
	const { id } = result;
	let icon = <ProductIcon size="small" product={ProductKeys.Focus} />;
	let resultType, title, highlightedTitle, metadata;

	if (isSearchResultMercuryFocusAreaPartial(result)) {
		const { focusArea } = result;

		icon = (
			<FocusAreaIcon
				status={focusArea?.status?.key as FocusAreaStatusKey}
				health={focusArea?.health?.key as FocusAreaHealthKey}
			/>
		);
		title = result.title;
		highlightedTitle =
			title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;
		resultType = 'Focus area';
		metadata = [
			'Focus',
			[
				'type',
				<Box xcss={typeLabelContainer}>
					<SearchResultTypeText
						type={result.type as SearchResultType}
						defaultMessage={resultType}
					/>
				</Box>,
			],
			result.focusArea ? ['focusAreaType', result.focusArea?.focusAreaType.name] : null,
		] satisfies SearchMetadataPart[];
	}

	if (isSearchResultMercuryFocusAreaStatusUpdatePartial(result)) {
		title = getSummarizedAdfContent(result.description, query);
		highlightedTitle =
			title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;
		resultType = 'Focus area update';
		metadata = [
			'Focus',
			[
				'type',
				<Box xcss={typeLabelContainer}>
					<SearchResultTypeText
						type={result.type as SearchResultType}
						defaultMessage={resultType}
					/>
				</Box>,
			],
		] satisfies SearchMetadataPart[];
	}

	if (isSearchResultSpfAskPartial(result)) {
		title = result.title;
		highlightedTitle =
			title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;
		resultType = 'Ask';
		metadata = [
			'Focus',
			[
				'type',
				<Box xcss={typeLabelContainer}>
					<SearchResultTypeText
						type={result.type as SearchResultType}
						defaultMessage={resultType}
					/>
				</Box>,
			],
		] satisfies SearchMetadataPart[];
	}

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	const url = getGenericUrlWithAnalyticsQueryParams({ url: result.url, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `${ProductKeys.Focus} ${result.type}`,
		product: ProductKeys.Focus,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapTalentSearchResult = (
	result: SearchTalentPartial,
	query: string,
	searchSessionId: string,
) => {
	const { id } = result;
	let icon = <ProductIcon size="small" />;
	let resultType, title, highlightedTitle, metadata;
	const querySplit = query.split(' ');

	if (isSearchTalentPartial(result)) {
		const { position } = result;

		let jobTitle: string = '';
		let externalPositionKey: string = '';
		let positionKey: string = '';
		const preferredName: string = position?.worker?.preferredName || '';

		icon = <TalentUserAvatar isQuickFind src={position?.worker?.user?.picture} />;

		for (const field of position?.fieldValues ?? []) {
			switch (field.fieldId) {
				case 'positionKey':
					if ('stringValue' in field.fieldValue) {
						positionKey = field.fieldValue.stringValue || '';
					}
					break;
				case 'positionPositionTitle':
					if ('stringValue' in field.fieldValue) {
						jobTitle = field.fieldValue.stringValue || '';
					}
					break;
				case 'positionExternalPositionKey':
					if ('stringValue' in field.fieldValue) {
						externalPositionKey = field.fieldValue.stringValue || '';
					}
					break;
				default:
					break;
			}
		}

		title = preferredName ? `${positionKey}: ${preferredName}` : positionKey;
		highlightedTitle =
			title && query ? highlightMatchedText(title, querySplit).highlightedTitle : undefined;
		resultType = 'Position';
		metadata = [
			'Talent',
			[
				'type',
				<Box xcss={typeLabelContainer}>
					<SearchResultTypeText
						type={result.type as SearchResultType}
						defaultMessage={resultType}
					/>
				</Box>,
			],
			['jobTitle', jobTitle],
			[
				'externalPositionKey',
				externalPositionKey ? (
					<FormattedMessage
						{...messages.talentExternalPositionKey}
						values={{ positionKey: externalPositionKey }}
					/>
				) : null,
			],
		] satisfies SearchMetadataPart[];
	}

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	const url = getGenericUrlWithAnalyticsQueryParams({ url: result.url, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `${ProductKeys.Talent} ${result.type}`,
		product: ProductKeys.Talent,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapThirdPartySearchResult = (
	result: SearchResultGraphDocument<SearchResultEntity>,
	query: string,
	searchSessionId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	const { id, providerId, integrationId, subtype, lastModifiedDate, title } = result;

	const product = get3PProductFromProviderIdOrIntegrationId({
		thirdPartyConfigs,
		providerId,
		integrationId,
	});

	if (!isProductKey(product)) {
		return;
	}

	const { resourceType } = AnyAri.parse(id);

	const icon = <ProductIcon size="small" product={product} />;

	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const typeLabel = getSubtypeLabel({
		product,
		subtype,
		resourceType,
	});

	const parentName = isDocumentEntity(result) ? result.entity?.parent?.displayName : null;

	/**
	 * Use constant config as fallback
	 * Loom is not included in thirdPartyConfigs from API
	 */
	const productDisplayName =
		thirdPartyConfigs[product as ProductKeys3P]?.displayName ||
		getConstantThirdPartyConfigs().get(product)?.displayName;

	const metadata = [
		['product', productDisplayName],
		[
			'type',
			typeLabel ? (
				<Box xcss={typeLabelContainer}>
					<FormattedMessage {...typeLabel} />
				</Box>
			) : subtype ? (
				<Box xcss={typeLabelContainer}>
					{subtype.charAt(0).toUpperCase() + subtype.substring(1).toLowerCase()}
				</Box>
			) : null,
		],
		parentName ?? null,
	] satisfies SearchMetadataPart[];

	const timestamp = lastModifiedDate || '';
	const eventType = WORKED_EVENT.UPDATED;
	const rightMetadata = lastModifiedDate ? (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={false} />
	) : null;
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const url = getGenericUrlWithAnalyticsQueryParams({ url: result.url, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `${product} ${subtype}`,
		product: product,
	};

	// Build title metadata parts (e.g. PR status lozenge)
	const titleMetadataParts: SearchMetadataPart[] = [];

	if (isPullRequestEntity(result)) {
		const prStatus = result.entity?.pullRequestStatus;

		if (prStatus) {
			titleMetadataParts.push([
				'status',
				<Lozenge
					appearance={getPullRequestStatusAppearance(prStatus)}
					testId="pull-request-status-lozenge"
				>
					<FormattedMessage {...getPullRequestStatusMessage(prStatus)} />
				</Lozenge>,
			]);
		}
	}

	if (isWorkItemEntity(result) && fg('qf_github_labels_in_search_results')) {
		const workItemStatus = result.entity?.status;

		if (workItemStatus) {
			if (fg('search_page_github_work_item_status_i18n')) {
				titleMetadataParts.push(['status', <GraphWorkItemStatusLozenge status={workItemStatus} />]);
			} else {
				// Status strings from third-party providers (e.g. GitHub) are typically
				// lower-snake/kebab-case (e.g. "in_progress"). Normalize for display so
				// the lozenge shows e.g. "In progress" instead of "in_progress".
				// Shared with the full search results page work-item renderer to keep
				// the two surfaces in lockstep.
				const statusLabel = normalizeWorkItemStatusLabel(workItemStatus);

				const statusNode = getStatus(statusLabel);
				if (statusNode) {
					titleMetadataParts.push(['status', statusNode]);
				}
			}
		}
	}

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
		...(titleMetadataParts.length > 0 ? { titleMetadataParts } : {}),
	};
};

const mapJiraSearchResult = (
	result: SearchJiraPartial,
	query: string,
	searchSessionId: string,
	isStatusLoading?: boolean,
	primaryProduct?: ProductKeys,
) => {
	const id = result.id;
	const analyticsMetadata = {
		contentId: id,
		contentType: `${ProductKeys.Jira} ${result.type}`,
		product: ProductKeys.Jira,
	};
	const isIssue = isSearchResultJiraIssue(result);
	const title = isIssue
		? [result.issue?.key, result.title].filter(Boolean).join(': ')
		: result.title;

	const issueIconUrl = isIssue ? result.issue?.issueTypeAvatarUrl : undefined;
	const boardIconUrl = isSearchResultJiraBoard(result) ? result.board?.iconUrl : undefined;

	const iconUrl = boardIconUrl || issueIconUrl || result.iconUrl;
	const icon = getJiraEntityIcon({ iconUrl, type: result.type, isIssue });

	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const status = isSearchResultJiraIssue(result)
		? getStatus(result.status?.name, { color: result.status?.colorName }, isStatusLoading)
		: null;

	const metadata: SearchMetadataPart[] = [];
	metadata.push('Jira');

	if (isSearchResultJiraIssue(result)) {
		const issueType = result.issue?.issueTypeField?.issueType;
		issueType && metadata.push(issueType.name);

		const project = result.issue?.fieldsById.edges[0]?.node.project.name;
		project && metadata.push(project);
	} else if (isSearchResultJiraBoard(result)) {
		metadata.push([
			'type',
			<Box xcss={typeLabelContainer}>
				<SearchResultTypeText type={result.type as SearchResultType} defaultMessage={result.type} />
			</Box>,
		]);

		const containerProjectName =
			result.container && 'projectName' in result.container && result.container.projectName;
		const containerUserName =
			result.container && 'userName' in result.container && result.container.userName;
		const containerName = containerProjectName || containerUserName || undefined;
		if (containerName) {
			metadata.push(containerName);
		}
	} else {
		const resultType = getJiraSearchResultTypeMetadata(result.type);
		metadata.push([
			'type',
			<Box xcss={typeLabelContainer}>
				<SearchResultTypeText type={resultType as SearchResultType} defaultMessage={result.type} />
			</Box>,
		]);
	}

	const rightMetadata = isSearchResultJiraIssue(result) ? (
		<EventTypeMessage
			timestamp={result.lastModifiedDate || ''}
			eventType={WORKED_EVENT.UPDATED}
			isMyActivityData={false}
		/>
	) : null;

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	let jiraResultUrl;
	if (isSearchResultJiraIssue(result)) {
		jiraResultUrl = result.issue?.webUrl || result.url;
	} else if (isSearchResultJiraBoard(result)) {
		const notDummyUrl = isDummyBoardUrl(result.url) ? undefined : result.url;
		jiraResultUrl = jiraResultUrl = result.board?.boardFrontendUrl || notDummyUrl;
		if (!jiraResultUrl) {
			// We don't want the board with no url to be displayed. Returning null prevents rendering
			return null;
		}
	} else {
		jiraResultUrl = result.url;
	}

	const customParams = primaryProduct?.includes(ProductKeys.Jira)
		? new Map<string, string>([['referrer', SearchExperience.QUICK_FIND]])
		: new Map<string, string>();
	const url = getGenericUrlWithAnalyticsQueryParams({
		url: jiraResultUrl,
		searchSessionId,
		customParams,
	});
	return {
		id,
		title,
		highlightedTitle,
		icon,
		status,
		analyticsMetadata,
		metadata,
		highlightedRightMetadata,
		rightMetadata,
		url,
	};
};

const mapAssetsSearchResult = (
	result: SearchAssetsPartial,
	query: string,
	searchSessionId: string,
) => {
	const id = result.id;

	const title = result.title;
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const metadata = isSearchResultAssetsObjectSchema(result)
		? ([
				'Assets',
				[
					'type',
					<Box xcss={typeLabelContainer}>
						<SearchResultTypeText
							type={result.type as SearchResultType}
							defaultMessage={result.type}
						/>
					</Box>,
				],
			] satisfies SearchMetadataPart[])
		: ([
				'Assets',
				[
					'type',
					<Box xcss={typeLabelContainer}>
						{isSearchResultAssetsObjectType(result) ? (
							`Object ${result.type}`
						) : (
							<SearchResultTypeText
								type={result.type as SearchResultType}
								defaultMessage={result.type}
							/>
						)}
					</Box>,
				],
				['schema', <Box xcss={typeLabelContainer}>{result.objectSchemaEntity.name}</Box>],
			] satisfies SearchMetadataPart[]);

	const timestamp = result.lastModifiedDate || '';
	const eventType = WORKED_EVENT.UPDATED;
	const rightMetadata = result.lastModifiedDate ? (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={false} />
	) : null;
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const getAssetsUrl = (result: SearchAssetsPartial) => {
		if (isSearchResultAssetsObjectSchema(result)) {
			return result.objectSchemaEntity.links.web;
		} else if (isSearchResultAssetsObjectType(result)) {
			return result.objectTypeEntity.links.web;
		} else {
			return result.objectEntity.links.web;
		}
	};
	let iconUrl: string | null = null;

	if (isSearchResultAssetsObjectSchema(result)) {
		iconUrl = null;
	} else if (isSearchResultAssetsObjectType(result)) {
		iconUrl = result.objectTypeEntity.icon.url48;
	} else if (isSearchResultAssetsObject(result)) {
		iconUrl = result.objectEntity.avatar.url48;
	}
	const icon = getAssetsEntityIcon({
		iconUrl,
		type: result.type,
		size: 'small',
	});

	const url = getGenericUrlWithAnalyticsQueryParams({ url: getAssetsUrl(result), searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `assets ${result.type}`,
		// Normalise to the canonical 1P key ('cmdb') so analytics pipelines
		// see a single identifier for Assets.
		product: fg('search-page-assets-analytics-consolidation') ? ProductKeys.Assets : 'assets',
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const CODE_PATH_MAX_LENGTH = 60;
const ELLIPSIS = '…';
const MIDDLE_ELLIPSIS = '/.../';

/**
 * Middle-truncates a filename so the start and end (including the extension)
 * stay visible. e.g. `aReallyLongFilename.ts` -> `aReallyLong…me.ts`.
 */
const truncateFilename = (name: string, maxLength: number): string => {
	if (name.length <= maxLength) {
		return name;
	}
	const keep = maxLength - ELLIPSIS.length;
	// Bias slightly toward the tail so the file extension is preserved.
	const head = Math.floor(keep / 2);
	const tail = keep - head;
	return `${name.slice(0, head)}${ELLIPSIS}${name.slice(name.length - tail)}`;
};

/**
 * Truncates a forward-slash separated path so it fits within `maxLength`.
 *
 * - Short paths pass through unchanged.
 * - Long multi-segment paths are middle-truncated by segment:
 *   `packages/foo/bar/baz/qux/test.js` -> `packages/.../qux/test.js`.
 * - If the leading segment alone is too large to be a useful prefix, it is
 *   dropped and the path is rebuilt from the right:
 *   `super-long-pkg-name-.../src/auth.ts` -> `.../src/auth.ts`.
 * - If a single segment (or the leaf filename) still overflows after segment
 *   truncation, the offending name is middle-truncated character-by-character
 *   with `truncateFilename`, preserving the start and the extension.
 */
const truncatePath = (fullPath: string, maxLength = CODE_PATH_MAX_LENGTH): string => {
	if (fullPath.length <= maxLength) {
		return fullPath;
	}

	const segments = fullPath.split('/');

	// Case: no slashes — just a long filename. Middle-truncate the filename.
	if (segments.length === 1) {
		return truncateFilename(fullPath, maxLength);
	}

	const leaf = segments[segments.length - 1];
	const first = segments[0];

	// Fit as many trailing segments as possible after `${prefix}/.../`.
	// `segments[0]` is intentionally excluded from `kept` in both modes:
	//   - prefix === first: already at the start, would duplicate
	//   - prefix === '':    first segment was deliberately dropped (too long)
	const buildFromRight = (prefix: string): string => {
		// When prefix is empty we emit `MIDDLE_ELLIPSIS.slice(1)` (no leading slash),
		// so account for the shorter prefix in the budget.
		const renderedMiddle = prefix ? MIDDLE_ELLIPSIS : MIDDLE_ELLIPSIS.slice(1);
		const prefixCost = prefix.length + renderedMiddle.length;
		const kept = [leaf];
		let length = prefixCost + leaf.length;
		for (let i = segments.length - 2; i > 0; i--) {
			const next = segments[i];
			if (length + next.length + 1 > maxLength) {
				break;
			}
			kept.unshift(next);
			length += next.length + 1;
		}
		return `${prefix}${renderedMiddle}${kept.join('/')}`;
	};

	// Case: first segment is too long to be a useful prefix — drop it.
	const minLeafBudget = leaf.length + MIDDLE_ELLIPSIS.length;
	if (first.length + minLeafBudget > maxLength) {
		// Use a leading "..." with no first segment, e.g. `.../src/auth.ts`.
		let result = `${MIDDLE_ELLIPSIS.slice(1)}${segments.slice(1).join('/')}`;
		if (result.length <= maxLength) {
			return result;
		}
		// Build from the right with an empty prefix; if even that overflows
		// (leaf itself is too long), middle-truncate the leaf.
		result = buildFromRight('');
		if (result.length > maxLength) {
			// `MIDDLE_ELLIPSIS.slice(1)` is what we actually emit (no leading "/").
			const leadingEllipsis = MIDDLE_ELLIPSIS.slice(1);
			const truncatedLeaf = truncateFilename(leaf, maxLength - leadingEllipsis.length);
			result = `${leadingEllipsis}${truncatedLeaf}`;
		}
		return result;
	}

	let result = buildFromRight(first);

	// Final safety net: if the leaf itself is too long, truncate it.
	if (result.length > maxLength) {
		const remaining = maxLength - first.length - MIDDLE_ELLIPSIS.length;
		const truncatedLeaf = truncateFilename(leaf, Math.max(remaining, ELLIPSIS.length + 2));
		result = `${first}${MIDDLE_ELLIPSIS}${truncatedLeaf}`;
	}

	return result;
};

const mapCodeSearchResult = (
	result: SearchResultGraphCode,
	query: string,
	searchSessionId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	const { id, title, url, lastModifiedDate, path, repository } = result;

	const product = 'github';
	const icon = <ProductIcon size="small" product={product} />;

	// Code results show the file path (e.g. `src/auth/auth.ts`) as the title, falling
	// back to the filename (`title`) when the path is missing. Long paths are middle-
	// truncated (e.g. `packages/.../test-dir/test.js`) so the row stays single-line.
	const displayTitle = (path ? truncatePath(path) : title) ?? '';

	const highlightedTitle =
		displayTitle && query
			? highlightMatchedText(displayTitle, query.split(' ')).highlightedTitle
			: undefined;

	const productDisplayName =
		thirdPartyConfigs[product as ProductKeys3P]?.displayName ||
		getConstantThirdPartyConfigs().get(product)?.displayName;

	const metadata = [
		['product', productDisplayName],
		[
			'type',
			<Box xcss={typeLabelContainer}>
				<FormattedMessage {...messages.code} />
			</Box>,
		],
		repository?.name ?? null,
	] satisfies SearchMetadataPart[];

	const timestamp = lastModifiedDate || '';
	const eventType = WORKED_EVENT.UPDATED;
	const rightMetadata = lastModifiedDate ? (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={false} />
	) : null;
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const finalUrl = getGenericUrlWithAnalyticsQueryParams({ url, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: 'github code',
		product,
	};

	return {
		id,
		icon,
		title: displayTitle,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url: finalUrl,
		analyticsMetadata,
	};
};

const mapSearchResult = (
	result: SearchPageNode,
	query: string,
	searchSessionId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isStatusLoading?: boolean,
	primaryProduct?: ProductKeys,
) => {
	if (isSearchConfluencePartial(result)) {
		return mapConfluenceSearchResult(result, query, searchSessionId);
	} else if (isSearchAtlasPartial(result)) {
		return mapAtlasSearchResult(result, query, searchSessionId);
	} else if (isSearchMercuryPartial(result)) {
		return mapMercurySearchResult(result, query, searchSessionId);
	} else if (isSearchTalentPartial(result) && fg('talent_global_search')) {
		return mapTalentSearchResult(result, query, searchSessionId);
	} else if (isCodeResult(result) && fg('rovo_github_code_search')) {
		return mapCodeSearchResult(result, query, searchSessionId, thirdPartyConfigs);
	} else if (isSearchResultGraphEntity(result)) {
		return mapThirdPartySearchResult(result, query, searchSessionId, thirdPartyConfigs);
	} else if (isSearchJiraPartial(result)) {
		return mapJiraSearchResult(result, query, searchSessionId, isStatusLoading, primaryProduct);
	} else if (isSearchAssetsPartial(result) && fg('assets_rovo_search_all')) {
		return mapAssetsSearchResult(result, query, searchSessionId);
	} else {
		return null;
	}
};

type MapSearchResultReturnType = ReturnType<typeof mapSearchResult>;

type Props = {
	result: SearchPageNode;
	state: State;
	onClick?: (props: QuickFindResultEvent) => void;
	onContextMenu?: (props: QuickFindResultEvent) => void;
	onHighlighted?: (props: QuickFindResultEvent) => void;
	resultType?: 'intentRouterWorkItem' | 'searchResults';
};

export const ProductEntity = ({
	result,
	state,
	onClick,
	onContextMenu,
	onHighlighted,
	resultType = 'searchResults',
}: Props): React.JSX.Element | null => {
	const quickFindQuery = useQuickFindQuery();
	const { setHoverCardOpen } = useQuickFindActions();
	const [primaryProduct] = usePrimaryProduct();
	const [primarySubProduct] = usePrimarySubProduct();
	const [searchSessionId] = useSearchSessionId();
	const [{ thirdPartyConfigs }] = useBootstrap();
	const isHydrationLoading = useIsJiraHydrationLoading();
	const { invalidateItem } = useItemsAnalyticsContext();

	const mappedResult = mapSearchResult(
		result,
		quickFindQuery,
		searchSessionId,
		thirdPartyConfigs,
		isHydrationLoading,
		primaryProduct,
	);

	const isImpersonated = isProductImpersonated(
		mappedResult?.analyticsMetadata.product as ProductKeys3P | undefined,
		thirdPartyConfigs,
	);

	const additionalAnalyticsAttributes = useMemo(
		() => ({
			metadata: {
				...mappedResult?.analyticsMetadata,
				connectorType: getResultConnectorSource({
					connectorType: result.connectorType,
					resultNodeId: result.id,
					thirdPartyConfigs,
					productKey: mappedResult?.analyticsMetadata.product,
				}),
			},
			is3pLink: is3pProductKeyForAnalytics(mappedResult?.analyticsMetadata.product),
			is3pSmartLink: isResultSmartLink({
				resultAri: result.id,
				thirdPartyConfigs,
				productKey: mappedResult?.analyticsMetadata.product,
				connectorType: result.connectorType,
			}),
		}),
		[mappedResult, result.id, thirdPartyConfigs, result.connectorType],
	);

	const onHoverCardVisibilityChange = useCallback(
		(isOpen: boolean) => {
			setHoverCardOpen(result.id, isOpen);
		},
		[setHoverCardOpen, result.id],
	);

	const onResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onClick?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes,
				isImpersonated,
			});

			onHoverCardVisibilityChange(false);
		},
		[additionalAnalyticsAttributes, isImpersonated, onClick, state, onHoverCardVisibilityChange],
	);

	const onResultContextMenu = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onContextMenu?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes,
				isImpersonated,
			});
		},
		[additionalAnalyticsAttributes, isImpersonated, onContextMenu, state],
	);

	const onResultHighlighted = useCallback(
		(data: SearchResultEventArguments) => {
			onHighlighted?.({
				data,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onHighlighted, state],
	);

	if (!mappedResult) {
		invalidateItem(result.id);
		return null;
	}

	const content = (
		<SearchResult
			{...mappedResult}
			resultType={resultType}
			primarySubProduct={primarySubProduct}
			product={mappedResult.analyticsMetadata.product}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onResultHighlighted={onResultHighlighted}
			connectorType={additionalAnalyticsAttributes.metadata.connectorType}
		/>
	);

	return isSearchResultJiraIssue(result) ? (
		<JiraResult
			result={result}
			content={content}
			mappedResult={mappedResult}
			onHoverCardVisibilityChange={onHoverCardVisibilityChange}
		/>
	) : (
		content
	);
};

interface JiraResultProps {
	onHoverCardVisibilityChange: (isOpen: boolean) => void;
	mappedResult: MapSearchResultReturnType;
	content: React.JSX.Element;
	result: SearchPageNode;
}

const JiraResult = ({
	result,
	content,
	mappedResult,
	onHoverCardVisibilityChange,
}: JiraResultProps) => {
	const { invalidateItem } = useItemsAnalyticsContext();
	const [primaryProduct] = usePrimaryProduct();
	const enableJiraIssueHoverCard =
		primaryProduct === ProductKeys.Jira && fg('enable_hover_card_jira_issues_quick_find');

	if (!mappedResult) {
		invalidateItem(result.id);
		return null;
	}

	return enableJiraIssueHoverCard ? (
		<ErrorBoundary fallback={content} section="result-hover-card">
			<Suspense fallback={content}>
				<HoverCard
					url={mappedResult.url}
					actionOptions={{
						hide: false,
						exclude: [CardAction.PreviewAction],
					}}
					role="dialog"
					titleId={mappedResult.title}
					label="quick-find-smart-hover-card"
					onVisibilityChange={onHoverCardVisibilityChange}
					shouldRenderToParent
				>
					{content}
				</HoverCard>
			</Suspense>
		</ErrorBoundary>
	) : (
		content
	);
};
